import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/input";
import { useRenameConversation } from "@/hooks/useRenameConversation";
import { PencilIcon } from "lucide-react";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

interface RenameConversationDialogProps {
  conversationId: string;
  currentName: string;
  trigger?: React.ReactNode;
}

export const RenameConversationDialog = ({
  conversationId,
  currentName,
  trigger,
}: RenameConversationDialogProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [newName, setNewName] = useState(currentName);
  const { mutate, isPending } = useRenameConversation(conversationId);

  useEffect(() => {
    if (currentName) {
      setNewName(currentName);
    }
  }, [currentName]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (newName.trim() && newName.trim() !== currentName && !isPending) {
      mutate(newName.trim(), {
        onSuccess: () => {
          setOpen(false);
        },
      });
    }
  };

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (isOpen) {
      setNewName(currentName);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="ghost" size="sm">
            <PencilIcon className="h-4 w-4" />
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="bg-[var(--card-background)] border-[1px] border-solid border-ring w-[calc(100vw-2rem)] max-w-md sm:max-w-lg">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{t("conversation.renameConversation")}</DialogTitle>
            <DialogDescription>
              {t("playground.renameConversationDescription")}
            </DialogDescription>
          </DialogHeader>
          <div className="relative py-4">
            <Input
              value={newName}
              onChange={(e) => setNewName(e.target.value.slice(0, 255))}
              maxLength={255}
              placeholder={t("playground.conversationName")}
              autoFocus
              className="w-full pr-14 rtl:pr-4 rtl:pl-14"
            />

            <p className="pointer-events-none absolute [inset-inline-end:0.5rem] top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
              {newName.length}/255
            </p>
          </div>
          <DialogFooter className="gap-2 sm:gap-0 flex-col sm:flex-row">
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              className="py-3 px-6 rounded-sm bg-transparent w-full sm:w-auto"
            >
              {t("common.cancel")}
            </Button>
            <Button
              type="submit"
              disabled={
                !newName.trim() || newName.trim() === currentName || isPending
              }
              className="py-3 px-6 rounded-sm w-full sm:w-auto"
            >
              {isPending ? t("playground.renaming") : t("common.rename")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
